import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { 
  Trophy, TrendingUp, Calendar, MapPin, Activity, 
  Share2, MessageSquare, Plus, Clock, Award, BarChart2, ChevronDown, ChevronRight,
  Dumbbell, ClipboardList, CalendarDays
} from 'lucide-react';
import { 
  LineChart, Line, BarChart, Bar, XAxis, YAxis, 
  CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';
import { AZURE } from '../services/api';
import { useSportsLang } from '../contexts/SportsLangContext';
import RaceSegmentCharts from '../components/swimming/RaceSegmentCharts';

// === CORES & TEMA ===
const THEME = {
  primary: '#111111',
  gold: '#b48c2c',
  bg: '#f3f2ef'
};

const LABELS = {
  pt: {
    follow: 'Seguir',
    swimmer: 'Nadador(a)',
    player: 'Jogador(a)',
    clubUnknown: 'Clube não informado',
    country: 'Brasil',
    medals: 'Medalhas',
    followers: 'Seguidores',
    competitions: 'Competições',
    evolutionTitle: (e) => `Evolução - ${e}`,
    evolutionEmpty: 'Nenhum dado de evolução disponível para este evento.',
    timeAxis: 'Tempo (s) x Data da Competição',
    splitsTitle: 'Análise de Splits (Parciais)',
    generalStats: 'Estatísticas Gerais',
    noStats: 'Sem dados suficientes para exibir estatísticas.',
    statJogos: 'Jogos', statMinutos: 'Minutos', statGols: 'Gols',
    statAssistencias: 'Assistências', statRating: 'Rating', statTitular: 'Titular',
    metricJogos: 'Jogos', metricMinutos: 'Minutos', metricGols: 'Gols',
    metricAssistencias: 'Assistências', metricRatingMedio: 'Rating Médio',
    metricFinalizacoes: 'Finalizações', metricPassesDecisivos: 'Passes Decisivos',
    evolutionByComp: 'Evolução por Competição',
    noResults: 'Nenhum resultado disponível.',
    histByComp: 'Histórico por Competição',
    histByCompSub: 'Fechado por campeonato e ano dos indicadores',
    compFallback: 'Competição',
    indJogos: 'Jogos', indMinutos: 'Minutos', indGols: 'Gols',
    indAssist: 'Assist.', indFinaliz: 'Finaliz.', indPassesD: 'Passes D.', indRating: 'Rating',
    gamesLbl: 'Jogos:', minLbl: 'Min:', ratingLbl: '• Rating:',
    histResults: 'Histórico de Resultados',
    bestTime: 'Melhor tempo:',
    podium: 'Pódio •',
    sportFootball: 'Futebol', sportSwim: 'Natação',
    loading: 'Carregando perfil...',
    notFound: 'Atleta não encontrado.',
    backToPeople: 'Voltar para Pessoas',
    breadcrumb: 'Pessoas',
    tabEvolution: 'Evolução & Stats', tabHistory: 'Histórico Completo',
    tabBio: 'Biomecânica', tabFeed: 'Feed Social',
    selectEvent: 'Selecione o Evento:',
    perfHighlight: 'Performance Destaque',
    perfHighlightSub: 'Acompanhe a evolução e quebre recordes!',
    feedDevTitle: 'Feed Social em Desenvolvimento',
    feedDevSub: 'Em breve você poderá ver posts, conquistas e interagir com outros atletas.',
    pbTitle: 'Melhores Marcas (PB)',
    noPbs: 'Nenhum recorde pessoal disponível.',
    career: 'Trajetória',
    current: 'Atual',
    noClubHistory: 'Histórico de clubes não disponível.',
    sidebarStats: 'Estatísticas',
    gold: 'Ouros', silver: 'Pratas', bronze: 'Bronzes',
    training: 'Treinamento',
    techTraining: 'Treino Técnico', techTrainingSub: 'Planos e execuções',
    physTraining: 'Treino Físico', physTrainingSub: 'Força, condicionamento',
    schedule: 'Cronograma', scheduleSub: 'Agenda da semana',
    dateLocale: 'pt-BR',
  },
  en: {
    follow: 'Follow',
    swimmer: 'Swimmer',
    player: 'Player',
    clubUnknown: 'Club not informed',
    country: 'Brazil',
    medals: 'Medals',
    followers: 'Followers',
    competitions: 'Competitions',
    evolutionTitle: (e) => `Evolution - ${e}`,
    evolutionEmpty: 'No evolution data available for this event.',
    timeAxis: 'Time (s) x Competition Date',
    splitsTitle: 'Splits Analysis',
    generalStats: 'General Statistics',
    noStats: 'Not enough data to display statistics.',
    statJogos: 'Games', statMinutos: 'Minutes', statGols: 'Goals',
    statAssistencias: 'Assists', statRating: 'Rating', statTitular: 'Starter',
    metricJogos: 'Games', metricMinutos: 'Minutes', metricGols: 'Goals',
    metricAssistencias: 'Assists', metricRatingMedio: 'Avg Rating',
    metricFinalizacoes: 'Shots', metricPassesDecisivos: 'Key Passes',
    evolutionByComp: 'Evolution by Competition',
    noResults: 'No results available.',
    histByComp: 'History by Competition',
    histByCompSub: 'Grouped by championship and year',
    compFallback: 'Competition',
    indJogos: 'Games', indMinutos: 'Min', indGols: 'Goals',
    indAssist: 'Ast.', indFinaliz: 'Shots', indPassesD: 'KP', indRating: 'Rating',
    gamesLbl: 'Games:', minLbl: 'Min:', ratingLbl: '• Rating:',
    histResults: 'Results History',
    bestTime: 'Best time:',
    podium: 'Podium •',
    sportFootball: 'Football', sportSwim: 'Swimming',
    loading: 'Loading profile...',
    notFound: 'Athlete not found.',
    backToPeople: 'Back to People',
    breadcrumb: 'People',
    tabEvolution: 'Evolution & Stats', tabHistory: 'Full History',
    tabBio: 'Biomechanics', tabFeed: 'Social Feed',
    selectEvent: 'Select Event:',
    perfHighlight: 'Performance Highlight',
    perfHighlightSub: 'Track your evolution and break records!',
    feedDevTitle: 'Social Feed in Development',
    feedDevSub: "Soon you'll be able to see posts, achievements and interact with other athletes.",
    pbTitle: 'Personal Bests (PB)',
    noPbs: 'No personal records available.',
    career: 'Career',
    current: 'Current',
    noClubHistory: 'Club history not available.',
    sidebarStats: 'Statistics',
    gold: 'Gold', silver: 'Silver', bronze: 'Bronze',
    training: 'Training',
    techTraining: 'Technical Training', techTrainingSub: 'Plans and executions',
    physTraining: 'Physical Training', physTrainingSub: 'Strength, conditioning',
    schedule: 'Schedule', scheduleSub: 'Weekly agenda',
    dateLocale: 'en-US',
  }
};

const parseTimeToSeconds = (timeValue) => {
  if (timeValue === null || timeValue === undefined) return null;
  if (typeof timeValue === 'number') return timeValue;
  if (typeof timeValue !== 'string') return null;
  const trimmed = timeValue.trim();
  if (!trimmed) return null;
  if (trimmed.includes(':')) {
    const [minutes, seconds] = trimmed.split(':');
    const minVal = parseFloat(minutes);
    const secVal = parseFloat(seconds);
    if (Number.isNaN(minVal) || Number.isNaN(secVal)) return null;
    return minVal * 60 + secVal;
  }
  const parsed = parseFloat(trimmed);
  return Number.isNaN(parsed) ? null : parsed;
};

const formatSeconds = (seconds) => {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return '-';
  const total = Number(seconds);
  const minutes = Math.floor(total / 60);
  const remainder = (total % 60).toFixed(2).padStart(5, '0');
  return minutes > 0 ? `${minutes}:${remainder}` : remainder;
};

// === COMPONENTE 1: HEADER DO ATLETA (PERFIL SOCIAL) ===
const AthleteHeader = ({ athlete, sport }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  const getAvatarUrl = () => {
    if (athlete.photo_url) return athlete.photo_url;
    if (athlete.avatar_url) return athlete.avatar_url;
    return null;
  };

  const avatarUrl = getAvatarUrl();
  const totalMedals = (athlete.gold_medals || 0) + (athlete.silver_medals || 0) + (athlete.bronze_medals || 0);
  const totalCompetitions = athlete.total_competitions || athlete.competitionCount || 0;

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden mb-6">
      {/* Capa CLARA (fundo cinza claro, não verde escuro) */}
      <div className="h-48 relative bg-gradient-to-r from-gray-100 to-gray-50">
        <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
        <div className="absolute top-4 right-4 z-20">
           <button className="px-4 py-2 bg-[#111111] text-white rounded-lg font-semibold hover:bg-[#0b0b0c] transition-colors flex items-center gap-2 text-xs shadow-sm">
             <Plus size={14} /> {t.follow}
           </button>
        </div>
      </div>

      {/* Info Principal */}
      <div className="px-6 pb-6 pt-6 relative">
        <div className="flex flex-col md:flex-row gap-6 items-start md:items-end -mt-16 mb-4">
          
          {/* Avatar */}
          <div className="w-32 h-32 rounded-full border-4 border-white shadow-lg bg-white overflow-hidden z-10 flex items-center justify-center flex-shrink-0">
            {avatarUrl ? (
              <img src={avatarUrl} alt={athlete.name} className="w-full h-full object-cover" />
            ) : (
              <span className="text-3xl font-bold text-gray-300">{athlete.name?.substring(0, 2).toUpperCase() || '?'}</span>
            )}
          </div>

          {/* Nome e Bio - ajustado padding para não sobrepor */}
          <div className="flex-1 md:pb-2 mt-2 md:mt-0">
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-xl md:text-2xl font-semibold text-gray-900 tracking-tight">{athlete.name}</h1>
              {athlete.verified && <div className="text-blue-500"><Award size={20} fill="currentColor" /></div>}
            </div>
            <p className="text-gray-500 text-sm mb-3">
              {sport === 'natacao' ? t.swimmer : t.player} • {athlete.current_club || athlete.club || t.clubUnknown} • {t.country}
            </p>
            
            {/* Stats Sociais */}
            <div className="flex gap-6 text-sm">
               <div className="text-center">
                 <span className="block font-semibold text-gray-900 text-base">{totalMedals}</span>
                 <span className="text-[11px] text-gray-500">{t.medals}</span>
               </div>
               <div className="text-center">
                 <span className="block font-semibold text-gray-900 text-base">156</span>
                 <span className="text-[11px] text-gray-500">{t.followers}</span>
               </div>
               <div className="text-center">
                 <span className="block font-semibold text-gray-900 text-base">{totalCompetitions}</span>
                 <span className="text-[11px] text-gray-500">{t.competitions}</span>
               </div>
            </div>
          </div>

          {/* Ações Rápidas */}
          <div className="flex gap-2 pb-2">
            <button className="p-2 rounded-lg border border-gray-100 hover:bg-gray-50 text-gray-600"><MessageSquare size={18} /></button>
            <button className="p-2 rounded-lg border border-gray-100 hover:bg-gray-50 text-gray-600"><Share2 size={18} /></button>
          </div>
        </div>
      </div>
    </div>
  );
};

// === COMPONENTE 2: GRÁFICO DE EVOLUÇÃO (TEMPO) ===
const EvolutionChart = ({ data, eventName }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  if (!data || data.length === 0) {
    return (
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm mb-6">
        <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2 mb-4">
          <TrendingUp className="text-[#111111]" />
          {t.evolutionTitle(eventName)}
        </h3>
        <p className="text-center text-gray-500 py-12">{t.evolutionEmpty}</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm mb-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
          <TrendingUp className="text-[#111111]" />
          {t.evolutionTitle(eventName)}
        </h3>
      </div>
      
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
            <XAxis dataKey="date" tick={{fontSize: 12}} stroke="#9ca3af" />
            <YAxis domain={['dataMin - 1', 'dataMax + 1']} tick={{fontSize: 12}} stroke="#9ca3af" />
            <Tooltip 
              contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
              labelStyle={{ color: '#6b7280', marginBottom: '4px' }}
            />
            <Line 
              type="monotone" 
              dataKey="time" 
              stroke={THEME.primary} 
              strokeWidth={3}
              dot={{ r: 4, fill: THEME.primary, strokeWidth: 2, stroke: '#fff' }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-center text-gray-400 mt-2">{t.timeAxis}</p>
    </div>
  );
};

// === COMPONENT 3: GRÁFICO DE SPLITS (BARRAS) ===
const SplitsChart = ({ splits }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  if (!splits || splits.length === 0) {
    return null;
  }

  return (
    <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm mb-6">
      <h3 className="font-bold text-lg text-gray-900 mb-6 flex items-center gap-2">
        <Activity className="text-[#b48c2c]" />
        {t.splitsTitle}
      </h3>
      
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={200}>
          <BarChart data={splits} layout="vertical">
             <CartesianGrid strokeDasharray="3 3" horizontal={true} stroke="#eee" />
             <XAxis type="number" hide />
             <YAxis dataKey="lap" type="category" width={60} tick={{fontSize: 12}} />
             <Tooltip cursor={{fill: 'transparent'}} />
             <Bar dataKey="time" fill={THEME.primary} radius={[0, 4, 4, 0]} barSize={20} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// === COMPONENTE 3.5: FOOTBALL STATS (NOVO) ===
const FootballStats = ({ playerId }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMetric, setSelectedMetric] = useState('gols');

  useEffect(() => {
    let isMounted = true;

    const fetchStats = async () => {
      try {
        setLoading(true);
        const { data } = await AZURE.get(`/football/player/${playerId}/stats/summary`);
        if (!isMounted) return;
        setStatsData(data?.success ? data : null);
      } catch (error) {
        console.error('[FootballStats] Failed to fetch stats:', error);
        if (isMounted) setStatsData(null);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (playerId) fetchStats();
    else setLoading(false);

    return () => {
      isMounted = false;
    };
  }, [playerId]);

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm mb-6">
        <div className="animate-pulse space-y-4">
          <div className="h-5 bg-gray-200 rounded w-1/3"></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-16 bg-gray-100 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!statsData?.success) {
    return (
      <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm mb-6">
        <h3 className="font-semibold text-base text-gray-900 mb-2 tracking-tight">{t.generalStats}</h3>
        <p className="text-sm text-gray-600">{t.noStats}</p>
      </div>
    );
  }

  const totals = statsData.totals_overall || {};
  const byComp = Array.isArray(statsData.by_competition) ? statsData.by_competition : [];

  const ratingOverallValue = typeof totals.rating_medio === 'number' ? totals.rating_medio : Number(totals.rating_medio);
  const ratingOverallDisplay = Number.isFinite(ratingOverallValue) ? ratingOverallValue.toFixed(2) : '-';

  // Cards com totais principais (SEM emojis - padrão UI/UX do site)
  const statCards = [
    { label: t.statJogos, value: Number(totals.jogos || 0) },
    { label: t.statMinutos, value: Number(totals.minutos || 0).toLocaleString(t.dateLocale) },
    { label: t.statGols, value: Number(totals.gols || 0) },
    { label: t.statAssistencias, value: Number(totals.assistencias || 0) },
    { label: t.statRating, value: ratingOverallDisplay },
    { label: t.statTitular, value: Number(totals.vezes_titular || 0) }
  ];

  // Métricas disponíveis para o gráfico de evolução
  const availableMetrics = [
    { id: 'jogos', label: t.metricJogos, color: '#3b82f6' },
    { id: 'minutos', label: t.metricMinutos, color: '#8b5cf6' },
    { id: 'gols', label: t.metricGols, color: '#10b981' },
    { id: 'assistencias', label: t.metricAssistencias, color: '#f59e0b' },
    { id: 'rating_medio', label: t.metricRatingMedio, color: '#ef4444' },
    { id: 'finalizacoes', label: t.metricFinalizacoes, color: '#06b6d4' },
    { id: 'passes_decisivos', label: t.metricPassesDecisivos, color: '#0ea5e9' }
  ];

  // Preparar dados para gráfico de evolução (linha temporal)
  const evolutionData = byComp.map(comp => {
    const leagueName = String(comp.league_name || 'N/D');
    const seasonText = String(comp.season || 'N/D');
    const label = `${leagueName} ${seasonText}`.trim();
    return {
      competicao: label.length > 26 ? label.substring(0, 23) + '...' : label,
      season: seasonText,
      jogos: Number(comp.jogos || 0),
      minutos: Number(comp.minutos || 0),
      gols: Number(comp.gols || 0),
      assistencias: Number(comp.assistencias || 0),
      rating_medio: Number(comp.rating_medio || 0),
      finalizacoes: Number(comp.finalizacoes || 0),
      passes_decisivos: Number(comp.passes_decisivos || 0)
    };
  });

  const currentMetric = availableMetrics.find(m => m.id === selectedMetric) || availableMetrics[2];

  return (
    <div className="space-y-6">
      {/* Cards de Totais */}
      <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
        <h3 className="font-semibold text-base text-gray-900 mb-4 flex items-center gap-2 tracking-tight">
          <BarChart2 className="text-[#111111]" />
          {t.generalStats}
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {statCards.map((card, i) => (
            <div key={i} className="bg-gray-50 border border-gray-100 rounded-lg p-3 text-center hover:border-[#111111] transition-colors">
              <div className="text-[11px] font-semibold text-gray-500 uppercase mb-1 tracking-wide">{card.label}</div>
              <div className="text-2xl font-semibold text-[#111111] leading-tight">{card.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Gráfico de Evolução por Competição com Seletor */}
      {evolutionData.length > 0 && (
        <div className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-semibold text-base text-gray-900 tracking-tight">{t.evolutionByComp}</h3>
            
            {/* Seletor de Métrica */}
            <div className="flex flex-wrap gap-2">
              {availableMetrics.map(metric => (
                <button
                  key={metric.id}
                  onClick={() => setSelectedMetric(metric.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border ${
                    selectedMetric === metric.id
                      ? 'bg-[#111111] text-white border-[#111111] shadow-sm'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {metric.label}
                </button>
              ))}
            </div>
          </div>
          
          <div className="h-80 w-full min-w-0">
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={260}>
              <LineChart data={evolutionData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                <XAxis 
                  dataKey="competicao" 
                  tick={{fontSize: 11}} 
                  angle={-15} 
                  textAnchor="end" 
                  height={80}
                />
                <YAxis tick={{fontSize: 12}} />
                <Tooltip 
                  contentStyle={{ 
                    borderRadius: '8px', 
                    border: 'none', 
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                    fontSize: '12px'
                  }}
                />
                <Line 
                  type="monotone" 
                  dataKey={selectedMetric}
                  stroke={currentMetric.color}
                  strokeWidth={3}
                  dot={{ r: 5, fill: currentMetric.color, strokeWidth: 2, stroke: '#fff' }}
                  activeDot={{ r: 7 }}
                  name={currentMetric.label}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

// === COMPONENTE 4: HISTÓRICO (TIMELINE) ===
const ResultHistory = ({ groups, isFootball, competitionData }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  const [openCompKey, setOpenCompKey] = useState(null);

  const safeGroups = Array.isArray(groups) ? groups : [];
  const safeCompetitionData = Array.isArray(competitionData) ? competitionData : [];

  const competitionGroups = useMemo(() => {
    if (!isFootball || safeCompetitionData.length === 0) return [];
    const map = new Map();
    for (const row of safeCompetitionData) {
      const season = String(row?.season ?? '');
      const league = String(row?.league_name ?? '');
      const key = `${season}__${league}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          season,
          league,
          row
        });
      }
    }
    return Array.from(map.values()).sort((a, b) => {
      const sa = Number(a.season);
      const sb = Number(b.season);
      if (Number.isFinite(sa) && Number.isFinite(sb) && sa !== sb) return sb - sa;
      if (a.season !== b.season) return String(b.season).localeCompare(String(a.season));
      return String(a.league).localeCompare(String(b.league));
    });
  }, [isFootball, safeCompetitionData]);

  const hasAnyResults = safeGroups.length > 0 || competitionGroups.length > 0;

  if (!hasAnyResults) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-12 text-center">
        <p className="text-gray-500">{t.noResults}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Histórico Completo por competição (somente futebol) - accordion */}
      {isFootball && competitionGroups.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-gray-100 bg-gray-50">
            <h3 className="font-semibold text-sm text-gray-900 uppercase tracking-wide">{t.histByComp}</h3>
            <p className="text-xs text-gray-500 mt-1">{t.histByCompSub}</p>
          </div>

          <div className="divide-y divide-gray-100">
            {competitionGroups.map((g) => {
              const row = g.row || {};
              const isOpen = openCompKey === g.key;
              const ratingNum = typeof row.rating_medio === 'number' ? row.rating_medio : Number(row.rating_medio);
              const ratingText = Number.isFinite(ratingNum) ? ratingNum.toFixed(2) : '-';

              const indicators = [
                { label: t.indJogos, value: Number(row.jogos || 0) },
                { label: t.indMinutos, value: Number(row.minutos || 0).toLocaleString(t.dateLocale) },
                { label: t.indGols, value: Number(row.gols || 0) },
                { label: t.indAssist, value: Number(row.assistencias || 0) },
                { label: t.indFinaliz, value: Number(row.finalizacoes || 0) },
                { label: t.indPassesD, value: Number(row.passes_decisivos || 0) },
                { label: t.indRating, value: ratingText },
              ];

              return (
                <div key={g.key} className="p-4">
                  <button
                    type="button"
                    onClick={() => setOpenCompKey(isOpen ? null : g.key)}
                    className="w-full flex items-center justify-between gap-3 rounded-lg border border-gray-100 hover:border-gray-200 bg-white px-4 py-3 text-left"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-semibold text-gray-900 truncate">{g.league || t.compFallback}</div>
                        <span className="text-xs text-gray-400">•</span>
                        <div className="text-xs font-semibold text-gray-600">{g.season || '-'}</div>
                      </div>
                      <div className="mt-1 text-[11px] text-gray-500">
                        {t.gamesLbl} <span className="font-semibold text-gray-700">{Number(row.jogos || 0)}</span>
                        {' '}• {t.minLbl} <span className="font-semibold text-gray-700">{Number(row.minutos || 0)}</span>
                        {' '}{t.ratingLbl} <span className="font-semibold text-[#111111]">{ratingText}</span>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      {isOpen ? (
                        <ChevronDown size={18} className="text-gray-600" />
                      ) : (
                        <ChevronRight size={18} className="text-gray-600" />
                      )}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="mt-3 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                      {indicators.map((item) => (
                        <div key={item.label} className="bg-gray-50 border border-gray-100 rounded-lg px-3 py-2 text-center">
                          <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">{item.label}</div>
                          <div className="text-base font-semibold text-gray-900 leading-tight">{item.value}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Histórico de Resultados */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 bg-gray-50 flex justify-between items-center">
          <h3 className="font-semibold text-sm text-gray-900 uppercase tracking-wide">{t.histResults}</h3>
        </div>
        <div className="divide-y divide-gray-100">
          {safeGroups.map((group) => (
            <div key={group.event} className="border-b border-gray-100 last:border-b-0">
              <div className="px-4 py-3 bg-gray-50/70 flex items-center justify-between">
                <div className="font-semibold text-sm text-gray-900">{group.event}</div>
                <div className="text-xs text-gray-500">
                  {t.bestTime} <span className="font-semibold text-[#111111]">{group.bestTimeDisplay}</span>
                </div>
              </div>
              <div className="divide-y divide-gray-100">
                {group.results.map((res, i) => (
                  <div key={`${group.event}-${i}`} className="p-4 hover:bg-gray-50 transition-colors flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-gray-900 text-sm mb-1">{res.event}</div>
                      <div className="text-xs text-gray-500 flex items-center gap-2">
                        <Calendar size={12} /> {res.date} • {res.competition}
                      </div>
                      {res.club && (
                        <div className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                          <MapPin size={12} /> {res.club}
                        </div>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-mono font-semibold text-[#111111]">{res.time}</div>
                      {res.pb && <span className="text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded font-semibold">PB</span>}
                      {res.position && res.position <= 3 && (
                        <div className="text-[11px] text-gray-500 mt-1 font-semibold">{t.podium} {res.position}º</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// === PÁGINA PRINCIPAL ===
export default function PlayerDetailPage() {
  const { id } = useParams();
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  const location = useLocation();
  const fallbackPerson = location.state?.person || null;
  const isFootball = location.pathname.startsWith('/jogador');
  const sportKey = isFootball ? 'futebol' : 'natacao';
  const sportLabel = isFootball ? t.sportFootball : t.sportSwim;
  const [activeTab, setActiveTab] = useState('evolution');
  const [loading, setLoading] = useState(true);
  const [athlete, setAthlete] = useState(null);
  const [results, setResults] = useState([]);
  const [evolutionData, setEvolutionData] = useState([]);
  const [splitsData, setSplitsData] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('');
  const [personalBests, setPersonalBests] = useState([]);
  const [clubHistory, setClubHistory] = useState([]);
  const [competitionStats, setCompetitionStats] = useState([]); // Dados de desempenho por competição (futebol)

  const groupedResults = useMemo(() => {
    const groups = results.reduce((acc, res) => {
      const key = res.event || 'Evento';
      if (!acc[key]) {
        acc[key] = {
          event: key,
          bestTimeSeconds: res.timeSeconds ?? Number.POSITIVE_INFINITY,
          bestTimeDisplay: res.time || '-',
          results: []
        };
      }
      const timeValue = res.timeSeconds ?? Number.POSITIVE_INFINITY;
      if (timeValue < acc[key].bestTimeSeconds) {
        acc[key].bestTimeSeconds = timeValue;
        acc[key].bestTimeDisplay = res.time || '-';
      }
      acc[key].results.push(res);
      return acc;
    }, {});

    return Object.values(groups)
      .map(group => ({
        ...group,
        results: group.results
          .slice()
          .sort((a, b) => {
            const timeDiff = (a.timeSeconds ?? Number.POSITIVE_INFINITY) - (b.timeSeconds ?? Number.POSITIVE_INFINITY);
            if (timeDiff !== 0) return timeDiff;
            return new Date(b.dateValue || 0) - new Date(a.dateValue || 0);
          })
      }))
      .sort((a, b) => a.bestTimeSeconds - b.bestTimeSeconds);
  }, [results]);

  const availableEvents = groupedResults.map(group => group.event);

  useEffect(() => {
    if (!selectedEvent && availableEvents.length > 0) {
      setSelectedEvent(availableEvents[0]);
    }
  }, [availableEvents, selectedEvent]);

  useEffect(() => {
    const fetchAthleteData = async () => {
      try {
        setLoading(true);
        setResults([]);
        setEvolutionData([]);
        setSplitsData([]);
        setSelectedEvent('');
        setPersonalBests([]);
        setClubHistory([]);

        if (isFootball) {
          // Buscar dados de estatísticas do jogador de futebol
          try {
            const statsResponse = await AZURE.get(`/football/player/${id}/stats/summary`);
            if (statsResponse.data?.success && statsResponse.data?.by_competition) {
              setCompetitionStats(statsResponse.data.by_competition);
            }
          } catch (error) {
            console.error('Erro ao buscar estatísticas do jogador:', error);
          }
          
          if (fallbackPerson) {
            setAthlete({
              name: fallbackPerson.name,
              club: fallbackPerson.team,
              current_club: fallbackPerson.team,
              photo_url: fallbackPerson.avatar || null,
              verified: fallbackPerson.badges?.includes('VERIFICADO') || false,
              gold_medals: 0,
              silver_medals: 0,
              bronze_medals: 0,
              total_competitions: 0
            });
          } else {
            setAthlete({
              name: 'Jogador',
              club: null,
              current_club: null,
              photo_url: null,
              verified: false,
              gold_medals: 0,
              silver_medals: 0,
              bronze_medals: 0,
              total_competitions: 0
            });
          }
          setResults([]);
          setPersonalBests([]);
          setClubHistory([]);
          return;
        }
        
        const [
          swimmerResponse,
          summaryResponse,
          historyResponse,
          pbsResponse,
          clubsResponse
        ] = await Promise.allSettled([
          AZURE.get(`/swimming/swimmers/${id}`),
          AZURE.get(`/swimming/swimmers/${id}/summary`),
          AZURE.get(`/swimming/swimmers/${id}/history`),
          AZURE.get(`/swimming/swimmers/${id}/pbs`),
          AZURE.get(`/swimming/swimmers/${id}/clubs-timeline`)
        ]);

        const swimmerPayload = swimmerResponse.status === 'fulfilled' ? swimmerResponse.value.data : null;
        const summaryPayload = summaryResponse.status === 'fulfilled' ? summaryResponse.value.data : null;
        const historyPayload = historyResponse.status === 'fulfilled' ? historyResponse.value.data : null;
        const pbsPayload = pbsResponse.status === 'fulfilled' ? pbsResponse.value.data : null;
        const clubsPayload = clubsResponse.status === 'fulfilled' ? clubsResponse.value.data : null;

        if (swimmerPayload?.success && swimmerPayload.swimmer) {
          const swimmerData = swimmerPayload.swimmer;
          const summaryData = summaryPayload?.summary || {};

          setAthlete({
            ...swimmerData,
            ...summaryData,
            name: swimmerData.display_name || swimmerData.name,
            current_club: swimmerData.current_club_name || swimmerData.current_club || swimmerData.club,
            club: swimmerData.current_club_name || swimmerData.current_club || swimmerData.club,
            photo_url: swimmerData.photo_url || swimmerData.avatar_url || null,
            verified: swimmerData.is_verified || false
          });
        }

        if (historyPayload?.success && Array.isArray(historyPayload.history)) {
          const formattedResults = historyPayload.history.map((r) => {
            const timeSeconds = r.time_seconds ?? parseTimeToSeconds(r.time_formatted);
            return {
              event: r.event_name || r.event,
              date: new Date(r.date_performed).toLocaleDateString(t.dateLocale),
              dateValue: r.date_performed,
              competition: r.competition_name || r.competition,
              club: r.club_name || r.club,
              time: r.time_formatted || formatSeconds(timeSeconds),
              timeSeconds,
              position: r.position,
              pb: r.is_personal_best || false,
              splits: r.splits || null
            };
          });
          setResults(formattedResults);
        }

        if (pbsPayload?.success && Array.isArray(pbsPayload.pbs)) {
          setPersonalBests(pbsPayload.pbs.map(pb => ({
            event: `${pb.distance_meters}m ${pb.stroke}`,
            time: pb.time_formatted || formatSeconds(pb.best_time_seconds),
            date: pb.date_performed ? new Date(pb.date_performed).getFullYear() : null
          })));
        }

        if (clubsPayload?.success && Array.isArray(clubsPayload.timeline)) {
          setClubHistory(clubsPayload.timeline.map(item => ({
            club_name: item.club_name,
            period: item.year ? `${item.year}` : t.current
          })));
        }
      } catch (error) {
        console.error('Erro ao carregar dados do atleta:', error);
        if (fallbackPerson) {
          setAthlete({
            name: fallbackPerson.name,
            club: fallbackPerson.team,
            current_club: fallbackPerson.team,
            photo_url: fallbackPerson.avatar || null,
            verified: fallbackPerson.badges?.includes('VERIFICADO') || false,
            gold_medals: 0,
            silver_medals: 0,
            bronze_medals: 0,
            total_competitions: 0
          });
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchAthleteData();
    }
  }, [id, isFootball, fallbackPerson]);

  useEffect(() => {
    if (selectedEvent && results.length > 0) {
      const eventResults = results
        .filter(r => r.event === selectedEvent)
        .map(r => {
          const timeInSeconds = r.timeSeconds ?? parseTimeToSeconds(r.time);
          
          return {
            date: r.date,
            dateValue: r.dateValue,
            time: timeInSeconds
          };
        })
        .filter(item => item.time !== null && item.time !== undefined && !Number.isNaN(item.time))
        .sort((a, b) => new Date(a.dateValue || a.date) - new Date(b.dateValue || b.date));
      
      setEvolutionData(eventResults);

      // Buscar splits do resultado mais recente deste evento
      const latestResult = results.find(r => r.event === selectedEvent && r.splits);
      if (latestResult && latestResult.splits) {
        const formattedSplits = latestResult.splits.map(split => ({
          lap: split.distance || split.lap,
          time: parseFloat(split.time)
        }));
        setSplitsData(formattedSplits);
      } else {
        setSplitsData([]);
      }
    }
  }, [selectedEvent, results]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: THEME.bg }}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#111111] mx-auto mb-4"></div>
          <p className="text-gray-600">{t.loading}</p>
        </div>
      </div>
    );
  }

  if (!athlete) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: THEME.bg }}>
        <div className="text-center">
          <p className="text-gray-600">{t.notFound}</p>
          <Link to="/jogadores" className="text-[#111111] hover:underline mt-4 inline-block">
            {t.backToPeople}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-12 font-sans" style={{ backgroundColor: THEME.bg }}>
      
      {/* Breadcrumb sticky top */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm text-gray-500">
          <Link to="/jogadores" className="hover:text-gray-900">{t.breadcrumb}</Link> / 
          <span className="font-bold text-gray-900">{athlete.name}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        
        {/* Header Social do Atleta (com banner verde) */}
        <AthleteHeader athlete={athlete} sport={sportKey} />

        <div className="flex gap-2 mb-6 border-b border-gray-200 pb-1 overflow-x-auto">
          {[
            { id: 'evolution', label: t.tabEvolution, icon: BarChart2 },
            { id: 'history', label: t.tabHistory, icon: Clock },
            ...(!isFootball ? [{ id: 'biomechanics', label: t.tabBio, icon: Activity }] : []),
            { id: 'timeline', label: t.tabFeed, icon: MessageSquare }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id 
                  ? 'border-[#111111] text-[#111111]' 
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          <div className="lg:col-span-8">
             {activeTab === 'evolution' && (
               <div className="space-y-6">
                 {/* Se for FUTEBOL, mostra stats de futebol */}
                 {isFootball && (
                   <FootballStats playerId={id} />
                 )}

                 {/* Se for NATAÇÃO, mostra seletor de evento + gráfico de evolução */}
                 {!isFootball && (
                   <>
                     {availableEvents.length > 1 && (
                       <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
                         <label className="block text-sm font-bold text-gray-700 mb-2">
                           {t.selectEvent}
                         </label>
                         <select 
                           value={selectedEvent}
                           onChange={(e) => setSelectedEvent(e.target.value)}
                           className="w-full bg-gray-50 border border-gray-200 rounded-lg px-4 py-2 text-sm"
                         >
                           {availableEvents.map(event => (
                             <option key={event} value={event}>{event}</option>
                           ))}
                         </select>
                       </div>
                     )}

                     <EvolutionChart data={evolutionData} eventName={selectedEvent} />
                     
                     {splitsData.length > 0 && <SplitsChart splits={splitsData} />}

                     <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-5 text-white shadow-md flex justify-between items-center">
                        <div>
                          <h4 className="font-bold text-lg mb-1">{t.perfHighlight}</h4>
                          <p className="text-blue-100 text-sm">{t.perfHighlightSub}</p>
                        </div>
                        <div className="text-3xl">🏊‍♂️</div>
                     </div>
                   </>
                 )}
               </div>
             )}

             {activeTab === 'history' && (
               <ResultHistory 
                 groups={groupedResults} 
                 isFootball={isFootball}
                 competitionData={competitionStats}
               />
             )}

             {activeTab === 'biomechanics' && !isFootball && (
               <RaceSegmentCharts athleteId={parseInt(id)} style={null} />
             )}

             {activeTab === 'timeline' && (
               <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
                 <MessageSquare size={48} className="mx-auto mb-4 text-gray-300" />
                 <h3 className="font-bold text-lg text-gray-900 mb-2">{t.feedDevTitle}</h3>
                 <p className="text-gray-500 text-sm">
                   {t.feedDevSub}
                 </p>
               </div>
             )}
          </div>

          <div className="lg:col-span-4 space-y-6">
             <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
               <h3 className="font-bold text-sm text-gray-900 mb-4 flex items-center gap-2">
                 <Trophy size={16} className="text-yellow-500" />
                 {t.pbTitle}
               </h3>
               {personalBests.length > 0 ? (
                 <div className="space-y-3">
                   {personalBests.slice(0, 5).map((pb, i) => (
                     <div key={i} className="flex justify-between items-center border-b border-gray-50 pb-2 last:border-0 last:pb-0">
                       <span className="text-sm text-gray-600 font-medium">{pb.event}</span>
                       <div className="text-right">
                         <span className="block font-bold text-[#111111]">{pb.time}</span>
                         <span className="text-[10px] text-gray-400">{pb.date}</span>
                       </div>
                     </div>
                   ))}
                 </div>
               ) : (
                 <p className="text-xs text-gray-500 text-center py-4">{t.noPbs}</p>
               )}
             </div>

             <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
               <h3 className="font-semibold text-sm text-gray-900 mb-3">{t.career}</h3>
               {clubHistory.length > 0 ? (
                 <div className="space-y-4">
                   {clubHistory.map((hist, i) => (
                     <div key={i} className="flex items-center gap-3">
                       <div className="w-2 h-2 rounded-full bg-gray-300"></div>
                       <div className="flex-1">
                         <p className="text-sm font-semibold text-gray-800">{hist.club_name || hist.club}</p>
                         <p className="text-xs text-gray-500">{hist.period || `${hist.start_year} - ${hist.end_year || t.current}`}</p>
                       </div>
                     </div>
                   ))}
                 </div>
               ) : athlete.current_club ? (
                 <div className="flex items-center gap-3">
                   <div className="w-2 h-2 rounded-full bg-[#111111]"></div>
                   <div className="flex-1">
                     <p className="text-sm font-semibold text-gray-800">{athlete.current_club}</p>
                     <p className="text-xs text-gray-500">{t.current}</p>
                   </div>
                 </div>
               ) : (
                 <p className="text-xs text-gray-500 text-center py-4">{t.noClubHistory}</p>
               )}
             </div>

             <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
               <h3 className="font-semibold text-sm text-gray-900 mb-3">{t.sidebarStats}</h3>
               <div className="space-y-3">
                 {athlete.gold_medals > 0 && (
                   <div className="flex justify-between items-center">
                     <span className="text-sm text-gray-600">{t.gold}</span>
                     <span className="font-semibold text-gray-900">{athlete.gold_medals}</span>
                   </div>
                 )}
                 {athlete.silver_medals > 0 && (
                   <div className="flex justify-between items-center">
                     <span className="text-sm text-gray-600">{t.silver}</span>
                     <span className="font-semibold text-gray-900">{athlete.silver_medals}</span>
                   </div>
                 )}
                 {athlete.bronze_medals > 0 && (
                   <div className="flex justify-between items-center">
                     <span className="text-sm text-gray-600">{t.bronze}</span>
                     <span className="font-semibold text-gray-900">{athlete.bronze_medals}</span>
                   </div>
                 )}
                 {athlete.total_competitions > 0 && (
                   <div className="flex justify-between items-center">
                     <span className="text-sm text-gray-600">{t.competitions}</span>
                     <span className="font-semibold text-gray-900">{athlete.total_competitions}</span>
                   </div>
                 )}
               </div>
             </div>

             {/* Training Quick Actions */}
             <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
               <h3 className="font-bold text-sm text-gray-900 mb-4 flex items-center gap-2">
                 <Activity size={16} className="text-blue-500" />
                 {t.training}
               </h3>
               <div className="space-y-2">
                 <Link
                   to="/athlete/training"
                   className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-all group"
                 >
                   <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#111111' }}>
                     <ClipboardList size={18} className="text-white" />
                   </div>
                   <div className="flex-1">
                     <p className="text-sm font-semibold text-gray-900 group-hover:text-[#111111]">{t.techTraining}</p>
                     <p className="text-xs text-gray-500">{t.techTrainingSub}</p>
                   </div>
                   <ChevronRight size={16} className="text-gray-400" />
                 </Link>
                 <Link
                   to="/treino-fisico"
                   className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-all group"
                 >
                   <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#3b82f6' }}>
                     <Dumbbell size={18} className="text-white" />
                   </div>
                   <div className="flex-1">
                     <p className="text-sm font-semibold text-gray-900 group-hover:text-[#3b82f6]">{t.physTraining}</p>
                     <p className="text-xs text-gray-500">{t.physTrainingSub}</p>
                   </div>
                   <ChevronRight size={16} className="text-gray-400" />
                 </Link>
                 <Link
                   to="/cronograma"
                   className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-all group"
                 >
                   <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#b48c2c' }}>
                     <CalendarDays size={18} className="text-white" />
                   </div>
                   <div className="flex-1">
                     <p className="text-sm font-semibold text-gray-900 group-hover:text-[#b48c2c]">{t.schedule}</p>
                     <p className="text-xs text-gray-500">{t.scheduleSub}</p>
                   </div>
                   <ChevronRight size={16} className="text-gray-400" />
                 </Link>
               </div>
             </div>
          </div>
        </div>
      </div>
    </div>
  );
}
